import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { Accordion, Button, Select, TextField, Tooltip } from '@cimpress/react-components';
import InputColor from 'react-input-color';
import { getAllBackgrounds } from 'src/selectors/layers';
import { toSelectOption, getHexColor } from 'src/util/misc';
import type { RootState } from 'src/store';
import type {
    SceneVariable_SceneVariable,
    SceneVariable_SceneVariableRule,
    SceneVariableCurrentLink,
} from 'src/types/sceneVariable';
import type { BackgroundType } from 'src/types/background';
import UploadFile from './UploadFile';
import { ASSET_ACTIONS, ASSET_ACTIONS_OPTIONS, SCENE_VARIABLE_TYPES, SCENE_VARIABLE_TYPES_OPTIONS } from './constants';
import {
    addSceneVariable,
    removeSceneVariable,
    addSceneVariableRule,
    removeSceneVariableRule,
    updateSceneVariableRule,
    startCropping,
    updateConstraint,
    endCropping,
} from './slice';
import getSceneVariation from './selectors/getSceneVariation';
import ErrorIcon from 'src/components/icons/ErrorIcon';
import type { AssetOption } from './EachProductVariable';
// / STYLES

const sceneVariablesConfigurationStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px',
};
const sceneVariablesConfigurationVariableContentCroppingStyles: React.CSSProperties = {
    display: 'flex',
    flexBasis: '100%',
    padding: '0 8px',
    flexDirection: 'column',
};
const sceneVariablesConfigurationVariableContentCroppingFormStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '8px',
};
const sceneVariablesConfigurationVariableContentCroppingFormItemStyles: React.CSSProperties = {
    flex: '40%',
};

const buildTitle = (title: string, actions?: React.ReactNode, errorMsg?: string) => (
    <div className='layers-header' data-testid={`purpose-variables-${title}-title`}>
        <div className='layers-header__text'>
            {errorMsg && (
                <Tooltip direction='top' contents={errorMsg}>
                    <ErrorIcon />
                </Tooltip>
            )}
            {title}
        </div>
        {actions}
    </div>
);

interface SelectOption {
    value: string;
    label: string;
}

interface SceneVariablesState {
    variables: unknown[];
    selectedPurpose?: { value?: string; label?: string } | null;
    error?: string;
}

interface Props {
    sceneVariables: SceneVariable_SceneVariable[];
    purposeOptions: SelectOption[];
    addSceneVariable: (payload: { purpose?: string }) => void;
    removeSceneVariable: (payload: { purpose: string }) => void;
    addSceneVariableRule: (payload: { purpose: string; type: string }) => void;
    removeSceneVariableRule: (payload: { purpose: string; rule: SceneVariable_SceneVariableRule }) => void;
    updateSceneVariableRule: (payload: {
        purpose: string;
        ruleId: number;
        type: string;
        value?: unknown;
        validity?: boolean;
    }) => void;
    // `value`/`label` really can be `undefined` here (assets like overlays/backgrounds/document
    // masks don't always have a name) -- same reasoning as `AssetOption`'s own doc comment, and the
    // same shared type `ProductVariables.tsx` uses for the identical data passed down from
    // `AttributesSelection.tsx`.
    assetOptions: AssetOption[];
    backgroundLayers: BackgroundType[];
    startCropping: (payload: { item: SceneVariable_SceneVariable; id: number }) => void;
    endCropping: () => void;
    updateConstraint: (payload: { field: string; value: number | boolean }) => void;
    croppingData: SceneVariableCurrentLink['cropping'];
    assetList: { name?: string }[];
}

const SceneVariables = (props: Props) => {
    const [state, setState] = useState<SceneVariablesState>({
        variables: [],
    });

    const addVariable = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (state.selectedPurpose) {
            props.addSceneVariable({ purpose: state.selectedPurpose.value });
            const newState = { ...state, error: undefined };
            newState.selectedPurpose = {};
            setState(newState);
        } else {
            const newState = { ...state, error: 'Please select a valid purpose' };
            setState(newState);
        }
    };

    const hasErrors = useMemo(
        () => props.sceneVariables.some((variable) => variable.rules.some((rule) => !!rule.errorMessage)),
        [props.sceneVariables],
    );

    const title = buildTitle(
        'Purpose Variables',
        undefined,
        hasErrors ? 'Please fix the errors in the purpose variables.' : '',
    );

    const saveVariableChange = (
        item: SceneVariable_SceneVariable,
        ruleId: number,
        selected: unknown,
        type: string,
        validity?: boolean,
    ) => {
        if (item && ruleId >= 0 && type) {
            props.updateSceneVariableRule({ purpose: item.purpose, ruleId, type, value: selected, validity });
        }
    };

    const purposeChanged = (selected: { value?: string; label?: string } | null) => {
        const newState = { ...state };
        newState.selectedPurpose = selected;
        setState(newState);
    };

    const activeVariable = props.sceneVariables.find((i) => i.active);

    const removeRule = (
        e: React.MouseEvent,
        item: SceneVariable_SceneVariable,
        rule: SceneVariable_SceneVariableRule,
    ) => {
        e.stopPropagation();
        if (item && rule) {
            props.removeSceneVariableRule({ purpose: item.purpose, rule });
        }
    };

    const onCroppingValueChange = (
        item: SceneVariable_SceneVariable,
        ruleId: number,
        field: string,
        value: unknown,
    ) => {
        props.updateSceneVariableRule({ purpose: item.purpose, ruleId, type: field, value });
    };

    const getAssetListOptions = (item: SceneVariable_SceneVariable) => {
        const assetAlreadyUsed = item.rules.filter((each) => !!each.asset).map((each) => each.asset!.value);
        const assetListOptions = props.assetOptions.filter(
            // `eachOption.value` can genuinely be `undefined` (see `AssetOption`'s doc comment);
            // `.includes()` just compares values, so this still correctly keeps such an option in
            // the list (undefined never matches a real asset value) exactly as it did untyped.
            (eachOption) => !assetAlreadyUsed.includes(eachOption.value as string),
        );
        return assetListOptions;
    };

    const isCroppingAlreadySet = (item: SceneVariable_SceneVariable) => {
        const croppingAlreadySet = item.rules.some((each) => each.type === SCENE_VARIABLE_TYPES.Cropping);
        return croppingAlreadySet;
    };

    const variableOptions = (item: SceneVariable_SceneVariable): SelectOption[] => {
        if (!getAssetListOptions(item).length) {
            // no more asset so only cropping possible
            return toSelectOption([SCENE_VARIABLE_TYPES.Cropping]) as SelectOption[];
        }
        if (isCroppingAlreadySet(item)) {
            return toSelectOption([SCENE_VARIABLE_TYPES['Asset Change']]) as SelectOption[];
        }
        return SCENE_VARIABLE_TYPES_OPTIONS as SelectOption[];
    };

    const getLayerDimensions = (item: SceneVariable_SceneVariable, index: number) => {
        if (item && item.rules[index].asset) {
            const theLayerDetails = props.backgroundLayers.find((each) => each.id === item.rules[index].asset?.value);
            if (theLayerDetails) {
                return {
                    width: theLayerDetails.width,
                    height: theLayerDetails.height,
                };
            }
            return {};
        }
        return {};
    };

    const onToggleCropping = (item: SceneVariable_SceneVariable, id: number) => {
        if (props.croppingData.enable) {
            props.endCropping();
        } else {
            props.startCropping({ item, id });
        }
    };

    const updateConstraint = (field: string, value: number | boolean) => {
        props.updateConstraint({ field, value });
    };
    const updateConstraintNumber = (field: string, value: string) => {
        try {
            const numValue = value !== '' ? parseFloat(value) : 0;
            props.updateConstraint({ field, value: numValue });
        } catch {
            console.warn('Error parsing constraint value');
        }
    };

    const getErrorMessage = useCallback(
        (rule: SceneVariable_SceneVariableRule) => {
            if (
                rule.type === SCENE_VARIABLE_TYPES['Asset Change'] &&
                rule.asset &&
                !props.assetList.some((each) => each.name === rule.asset?.label)
            ) {
                return 'Selected asset is no longer a valid option. Please select another asset or remove the rule.';
            }

            return '';
        },
        [props.assetList],
    );

    useEffect(() => {
        props.sceneVariables.forEach((config) =>
            config.rules?.forEach((rule) => {
                const errMessage = getErrorMessage(rule);
                if (errMessage !== rule.errorMessage) {
                    saveVariableChange(config, rule.id, errMessage, 'errorMessage');
                }
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.assetList]);

    const renderRules = (item: SceneVariable_SceneVariable) =>
        item && item.rules ? (
            item.rules.map((rule) => {
                const assetListOptions = getAssetListOptions(item);
                const isAssetChange = rule.type === SCENE_VARIABLE_TYPES['Asset Change'];
                const isCropping = rule.type === SCENE_VARIABLE_TYPES.Cropping;

                const subtitle = buildTitle(
                    `Rule ${rule.id + 1}`,
                    <Button variant='anchor' onClick={(e) => removeRule(e, item, rule)}>
                        <img src='https://static.ux.cimpress.io/assets/icons/bin-1-l.svg' alt='delete' />
                    </Button>,
                    rule.errorMessage,
                );
                const isCroppingToolActive =
                    props.croppingData.ruleId === rule.id && props.croppingData.purpose === item.purpose;
                const isCroppingDisable = !isCroppingToolActive && props.croppingData.enable;
                // eslint-disable-next-line no-nested-ternary
                const croppingLabel = isCroppingToolActive ? 'End Cropping Tool' : 'Use Cropping Tool';
                return (
                    <div className='scene-variables-configuration-rules-content' key={rule.id}>
                        {subtitle}
                        <Select
                            options={variableOptions(item)}
                            menuPortalTarget={document.body}
                            label='Select a Variable'
                            className='scene-variables-configuration-rules-content_select'
                            onChange={(sel) => saveVariableChange(item, rule.id, sel.value, 'type')}
                            value={toSelectOption(rule.type)}
                        />
                        {isAssetChange && (
                            <>
                                <div data-testid={`purpose-variable-rule-${rule.id + 1}-asset-selector`}>
                                    <Select
                                        options={assetListOptions}
                                        menuPortalTarget={document.body}
                                        label='Select an Asset'
                                        onChange={(sel) => saveVariableChange(item, rule.id, sel, 'asset')}
                                        value={rule.asset}
                                    />
                                </div>
                                <Select
                                    // `toSelectOption`'s declared return type also covers its single-value (string
                                    // input) branch; `ASSET_ACTIONS_OPTIONS` is always built from an array, so
                                    // asserting the real array shape here instead of the loose/ambiguous union.
                                    options={ASSET_ACTIONS_OPTIONS as SelectOption[]}
                                    menuPortalTarget={document.body}
                                    label='Select an Action'
                                    onChange={(sel) => saveVariableChange(item, rule.id, sel.value, 'action')}
                                    value={toSelectOption(rule.action ?? '')}
                                />
                                {rule.action === ASSET_ACTIONS.REPLACE && (
                                    <UploadFile
                                        onChange={(fileDetails: unknown) =>
                                            saveVariableChange(item, rule.id, fileDetails, 'file')
                                        }
                                        // `rule.file`'s shared type leaves `name`/`imageUrl` individually optional,
                                        // but this is only ever written as a full `{ name, imageUrl }` object (see
                                        // `UploadFile`'s `onChange` above) -- asserting the real shape here instead
                                        // of the looser union.
                                        file={rule.file as { name: string; imageUrl: string } | undefined}
                                        {...getLayerDimensions(item, rule.id)}
                                    />
                                )}
                                {rule.action === ASSET_ACTIONS.FILL && (
                                    <InputColor
                                        initialValue={rule.fillColor || '#808080'}
                                        onChange={(evt) =>
                                            saveVariableChange(item, rule.id, getHexColor(evt.hex), 'fillColor')
                                        }
                                        placement='top'
                                    />
                                )}
                            </>
                        )}
                        {isCropping && (
                            <div
                                className='scene-variables-configuration-variables-content-cropping'
                                style={sceneVariablesConfigurationVariableContentCroppingStyles}>
                                <h3>Please use the canvas to select the Cropping Area or use the coordinates below</h3>
                                <div
                                    className='scene-variables-configuration-variables-content-cropping-form'
                                    style={sceneVariablesConfigurationVariableContentCroppingFormStyles}>
                                    <div style={sceneVariablesConfigurationVariableContentCroppingFormItemStyles}>
                                        <span>X0:</span>
                                        <TextField
                                            type='number'
                                            value={rule.x0}
                                            onChange={(event) =>
                                                onCroppingValueChange(item, rule.id, 'x0', event.target.value)
                                            }
                                        />
                                    </div>
                                    <div>
                                        <span>Y0:</span>
                                        <TextField
                                            type='number'
                                            value={rule.y0}
                                            onChange={(event) =>
                                                onCroppingValueChange(item, rule.id, 'y0', event.target.value)
                                            }
                                        />
                                    </div>
                                    <div style={sceneVariablesConfigurationVariableContentCroppingFormItemStyles}>
                                        <span>X1:</span>
                                        <TextField
                                            type='number'
                                            value={rule.x1}
                                            onChange={(event) =>
                                                onCroppingValueChange(item, rule.id, 'x1', event.target.value)
                                            }
                                        />
                                    </div>
                                    <div>
                                        <span>Y1:</span>
                                        <TextField
                                            type='number'
                                            value={rule.y1}
                                            onChange={(event) =>
                                                onCroppingValueChange(item, rule.id, 'y1', event.target.value)
                                            }
                                        />
                                    </div>
                                </div>
                                <div className='scene-variables-configuration-variables-content-cropping-constraint-wrapper'>
                                    <span>Constraint Dimensions</span>{' '}
                                    <input
                                        type='checkbox'
                                        className='scene-variables-configuration-variables-content-cropping-constraint-check'
                                        onChange={(e) => updateConstraint('enable', e.target.checked)}
                                    />
                                    <div className='scene-variables-configuration-variables-content-cropping-constraint-form'>
                                        <TextField
                                            label='Width'
                                            type='number'
                                            value={props.croppingData.constraint.width}
                                            onChange={(e) => updateConstraintNumber('width', e.target.value)}
                                        />
                                        <TextField
                                            label='Height'
                                            type='number'
                                            value={props.croppingData.constraint.height}
                                            onChange={(e) => updateConstraintNumber('height', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <Button
                                    variant='primary'
                                    disabled={isCroppingDisable}
                                    onClick={() => onToggleCropping(item, rule.id)}>
                                    {croppingLabel}
                                </Button>
                            </div>
                        )}
                    </div>
                );
            })
        ) : (
            <></>
        );

    const addRule = (item: SceneVariable_SceneVariable) => {
        if (item) {
            const options = variableOptions(item);
            if (options.length === 1 && options[0].value === 'Cropping') {
                props.addSceneVariableRule({ purpose: item.purpose, type: SCENE_VARIABLE_TYPES.Cropping });
            } else {
                props.addSceneVariableRule({ purpose: item.purpose, type: SCENE_VARIABLE_TYPES['Asset Change'] });
            }
        }
    };

    const isAllRuleConfigured = (item: SceneVariable_SceneVariable) => {
        if (isCroppingAlreadySet(item) && !getAssetListOptions(item).length) {
            return true;
        }
        return false;
    };

    const removeItem = (e: React.MouseEvent, item: SceneVariable_SceneVariable) => {
        e.stopPropagation();
        if (item) {
            props.removeSceneVariable({ purpose: item.purpose });
        }
    };

    const availablePurposes = props.purposeOptions.filter(
        (item) => props.sceneVariables.findIndex((variable) => variable.purpose === item.value) < 0,
    );

    return (
        <Accordion title={title} defaultOpen={false}>
            <div className='scene-variables-configuration' style={sceneVariablesConfigurationStyles}>
                {!activeVariable && (
                    <div data-testid='purpose-variable-selector' className='scene-variables-configuration-add'>
                        <Select
                            options={availablePurposes}
                            menuPortalTarget={document.body}
                            label='Select a Purpose'
                            onChange={purposeChanged}
                            value={state.selectedPurpose}
                        />
                        <Button data-testid='purpose-variable-add-btn' onClick={addVariable} variant='link'>
                            <img src='https://static.ux.cimpress.io/assets/icons/add-circle-1.svg' alt='Add Variable' />{' '}
                            Add Variable
                        </Button>
                        {state.error && <span className='scene-variables-configuration-error'>{state.error}</span>}
                    </div>
                )}
                <div className='list-variables'>
                    {props.sceneVariables.map((i) => {
                        const itemTitle = buildTitle(
                            i.purpose,
                            <Button variant='anchor' onClick={(e) => removeItem(e, i)}>
                                <img className='iconLg' src='https://static.ux.cimpress.io/assets/icons/bin-1-l.svg' />
                            </Button>,
                            hasErrors ? 'One or more rules have errors.' : '',
                        );
                        return (
                            <Accordion key={i.purpose} title={itemTitle} defaultOpen={true}>
                                <div className='scene-variables-configuration-rules'>{renderRules(i)}</div>
                                <div className='scene-variables-configuration-add'>
                                    <Button
                                        data-testid='purpose-variable-add-rule-btn'
                                        onClick={() => addRule(i)}
                                        variant='link'
                                        disabled={isAllRuleConfigured(i)}>
                                        <img
                                            src='https://static.ux.cimpress.io/assets/icons/add-circle-1.svg'
                                            alt='Add Variable'
                                        />
                                        Add Rule
                                    </Button>
                                </div>
                            </Accordion>
                        );
                    })}
                </div>
            </div>
        </Accordion>
    );
};

function mapStateToProps(state: RootState) {
    const { sceneVariables, cropping } = getSceneVariation(state);
    const backgroundLayers = getAllBackgrounds(state);
    return {
        sceneVariables,
        backgroundLayers,
        croppingData: cropping,
    };
}

export default connect(mapStateToProps, {
    addSceneVariable,
    removeSceneVariable,
    addSceneVariableRule,
    removeSceneVariableRule,
    updateSceneVariableRule,
    updateConstraint,
    startCropping,
    endCropping,
})(SceneVariables);
